import { useEffect, useState } from 'react';

import SGConstants from './SGConstants';
import { compareAlphabetical, compareDate, compareLength, compareNumerical, compareTime } from './SortingUtils';

const INITIAL_SORTABLE_GRID_STATE = {
  sortedGridData: [],
  targetField: '',
  sortDirection: SGConstants.SORT_DIRECTION_NONE,
  sortType: ''
};

const useSortableGrid = (gridData) => {
  const [sortableGridState, setSortableGridState] = useState(INITIAL_SORTABLE_GRID_STATE);

  const onColumnClicked = (columnField, sortType) => {
    const newSortDirection = getNewSortDirection(columnField, sortableGridState.sortDirection);

    setSortableGridState({
      ...sortableGridState,
      sortedGridData: sortGridData(sortableGridState.sortedGridData, columnField, sortType, newSortDirection),
      sortDirection: newSortDirection,
      targetField: columnField,
      sortType
    });
  };

  useEffect(() => {
    if (Array.isArray(gridData)) {
      setSortableGridState({
        ...sortableGridState,
        sortedGridData: sortGridData(JSON.parse(JSON.stringify(gridData)), sortableGridState.targetField, sortableGridState.sortType, sortableGridState.sortDirection)
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridData]);

  function sortGridData(dataToSort, targetField, sortType, sortDirection) {
    if (sortDirection !== SGConstants.SORT_DIRECTION_NONE && targetField && sortType) {
      switch (sortType) {
        case SGConstants.SORT_TYPE_ALPHABETICAL:
          return sortGridAlphabetical(dataToSort, targetField, sortDirection);
        case SGConstants.SORT_TYPE_NUMERICAL:
          return sortGridNumerical(dataToSort, targetField, sortDirection);
        case SGConstants.SORT_TYPE_DATE:
          return sortGridDate(dataToSort, targetField, sortDirection);
        case SGConstants.SORT_TYPE_TIME:
          return sortGridTime(dataToSort, targetField, sortDirection);
        case SGConstants.SORT_TYPE_LENGTH:
          return sortGridLength(dataToSort, targetField, sortDirection);
        default:
          return dataToSort;
      };
    } else {
      if (Array.isArray(gridData)) {
        return JSON.parse(JSON.stringify(gridData));
      } else {
        return [];
      }
    }
  };

  function sortGridAlphabetical(dataToSort, targetField, sortDirection) {
    if (sortDirection === SGConstants.SORT_DIRECTION_DESCENDING) {
      return dataToSort.sort((a, b) => compareAlphabetical(a[targetField], b[targetField]) * -1);
    } else {
      return dataToSort.sort((a, b) => compareAlphabetical(a[targetField], b[targetField]));
    }
  };

  function sortGridNumerical(dataToSort, targetField, sortDirection) {
    if (sortDirection === SGConstants.SORT_DIRECTION_DESCENDING) {
      return dataToSort.sort((a, b) => compareNumerical(a[targetField], b[targetField]) * -1);
    } else {
      return dataToSort.sort((a, b) => compareNumerical(a[targetField], b[targetField]));
    }
  };

  function sortGridDate(dataToSort, targetField, sortDirection) {
    if (sortDirection === SGConstants.SORT_DIRECTION_DESCENDING) {
      return dataToSort.sort((a, b) => compareDate(a[targetField], b[targetField]) * -1);
    } else {
      return dataToSort.sort((a, b) => compareDate(a[targetField], b[targetField]));
    }
  };

  function sortGridTime(dataToSort, targetField, sortDirection) {
    if (sortDirection === SGConstants.SORT_DIRECTION_DESCENDING) {
      return dataToSort.sort((a, b) => compareTime(a[targetField], b[targetField]) * -1);
    } else {
      return dataToSort.sort((a, b) => compareTime(a[targetField], b[targetField]));
    }
  };

  function sortGridLength(dataToSort, targetField, sortDirection) {
    if (sortDirection === SGConstants.SORT_DIRECTION_DESCENDING) {
      return dataToSort.sort((a, b) => compareLength(a[targetField], b[targetField]) * -1);
    } else {
      return dataToSort.sort((a, b) => compareLength(a[targetField], b[targetField]));
    }
  };

  function getNewSortDirection(targetField, sortDirection) {
    if (targetField === sortableGridState.targetField) {
      switch (sortDirection) {
        case SGConstants.SORT_DIRECTION_NONE: // first click
          return SGConstants.SORT_DIRECTION_ASCENDING;
        case SGConstants.SORT_DIRECTION_ASCENDING: // second click
          return SGConstants.SORT_DIRECTION_DESCENDING;
        default: // third click + catch
          return SGConstants.SORT_DIRECTION_NONE
      }
    } else { // first click
      return SGConstants.SORT_DIRECTION_ASCENDING;
    }
  };

  return {
    sortableGridState,
    onColumnClicked
  };
};

export default useSortableGrid;